import {
  Box,
  Heading,
  Tabs,
  TabList,
  Button,
  useTab,
  useMultiStyleConfig,
  Flex,
} from '@chakra-ui/react';
import React from 'react';
import { Link, useMatch } from 'react-router-dom';

import { PermissionGuard } from '~/core/PermissionGuard';
import { permissions as accountingIntegrationsPermissions } from '~/core/routes/settings/accounting-integrations/_index';
import { permissions as assignmentStrategyPermissions } from '~/core/routes/settings/assignment-strategy/Router';
import { permissions as bankAccountsPermissions } from '~/core/routes/settings/bank-accounts/_index';
import { permissions as bankIntegrationsPermissions } from '~/core/routes/settings/bank-integrations/_index';
import { permissions as creditFacilitiesPermissions } from '~/core/routes/settings/credit-facilities/permissions';
import { permissions as fundingRequestPermissions } from '~/core/routes/settings/funding-requests/permissions';
import { permissions as implementationPermissions } from '~/core/routes/settings/implementations/Router';
import { permissions as notificationsPermissions } from '~/core/routes/settings/notifications/_index';
import { permissions as webhooksPermissions } from '~/core/routes/settings/webhooks/_index';

// Fragments
type NavTabProps = { children: React.ReactNode; end?: boolean; to: string };
const NavTab = ({ children, to, end = false }: NavTabProps) => {
  const match = useMatch({ path: to, end });
  const tabProps = useTab({});
  const styles = useMultiStyleConfig('Tabs', tabProps);
  return (
    <Button
      __css={styles.tab}
      _active={{ fontWeight: 'bold', borderBottomColor: 'black' }}
      borderBottom="2px solid transparent"
      as={Link}
      isActive={!!match}
      alignItems="center"
      display="inline-flex"
      justifyContent="center"
      margin={0}
      textAlign="center"
      to={to}
    >
      {children}
    </Button>
  );
};

// Exports
type Props = { children: React.ReactNode };
export const SettingsViewTemplate = ({ children }: Props) => {
  return (
    <Flex direction="column" padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}>
      <Heading as="h2">Settings</Heading>
      <Tabs variant="line" colorScheme="black">
        <TabList
          overflowX="auto"
          gap="2rem"
          paddingTop="2rem"
          style={{ scrollbarWidth: 'thin' }}
        >
          {/* Common settings (borrower + capProvider + valleyBank) */}
          <NavTab to="/settings/my-account">User Information</NavTab>

          {/* Credit Facility & Credit Agreement Settings */}
          <PermissionGuard permissions={creditFacilitiesPermissions}>
            <NavTab to="/settings/credit-facilities">Facilities</NavTab>
          </PermissionGuard>

          {/* Borrower settings */}
          <PermissionGuard permissions={bankAccountsPermissions}>
            <NavTab to="/settings/bank-accounts">Bank Accounts</NavTab>
          </PermissionGuard>
          <PermissionGuard permissions={bankIntegrationsPermissions}>
            <NavTab to="/settings/bank-integrations">Bank Integrations</NavTab>
          </PermissionGuard>
          <PermissionGuard permissions={accountingIntegrationsPermissions}>
            <NavTab to="/settings/accounting-integrations">
              Accounting Integrations
            </NavTab>
          </PermissionGuard>
          <PermissionGuard permissions={notificationsPermissions}>
            <NavTab to="/settings/notifications">Notifications</NavTab>
          </PermissionGuard>
          <PermissionGuard permissions={webhooksPermissions}>
            <NavTab to="/settings/webhooks">Webhooks</NavTab>
          </PermissionGuard>
          <PermissionGuard permissions={assignmentStrategyPermissions}>
            <NavTab to="/settings/assignment-strategy">
              Assignment Strategy
            </NavTab>
          </PermissionGuard>
          <PermissionGuard permissions={implementationPermissions}>
            <NavTab to="/settings/implementations">Implementations</NavTab>
          </PermissionGuard>

          {/* Funding Request settings */}
          <PermissionGuard permissions={fundingRequestPermissions}>
            <NavTab to="/settings/funding-requests">Funding Requests</NavTab>
          </PermissionGuard>
        </TabList>
      </Tabs>
      <Box mt="25px">{children}</Box>
    </Flex>
  );
};
